// components/Maps/DropoffMap.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Where this order is going, who the pin can be sent to, and how far to trust
// it. One block under the drop-off row on every order screen.
//
// WHAT IT SHOWS, AND WHY EACH PART EARNS ITS PLACE
//
//   The place name. "0.3813715, 32.6511365" is the truth the delivery fee was
//   measured against, and it is unreadable. The numbers are still there, in the
//   tooltip and inside the shared message, but what a dispatcher reads is
//   "Kisaasi Road, Kyanja". Resolved once per pin and cached for 30 days —
//   see utilities/reverseGeocode.js, every lookup is billed.
//
//   Share. Staff brief riders over WhatsApp. When a rider is already on the
//   order we hold their number, so the button opens a chat with THAT rider,
//   message written; with nobody assigned it opens WhatsApp's own chooser.
//   Copy puts the same text on the clipboard for a dispatch group or a call.
//   The phone goes through utilities/phoneNumber.js: wa.me rejects the stored
//   "0700771301" outright, and a share button that opens an empty WhatsApp is
//   worse than no share button.
//
//   The precision chip stays. It is not decoration: it says whether the pin is
//   a live GPS fix or a geocoder's guess at an address, which is exactly what
//   decides whether a rider should ring before setting off.
//
//   The map is collapsed, and MOUNTED ONLY ONCE OPENED. A map on every order
//   lengthens an already long screen; the Maps JS API bills per map load, so
//   drawing one for every order view is a bill for a picture nobody asked to
//   see; and a map created inside a hidden container renders as grey tiles,
//   which mounting-on-open avoids entirely. It is torn down on close, so a
//   long shift working through orders does not keep every map it opened alive.
//
// CLASSIC `google.maps.Marker`, not AdvancedMarkerElement: advanced markers
// refuse to render on a map built without a Cloud Map ID, and that ID is baked
// in at BUILD time, so on a build where it was not set an advanced marker
// gives a map with no pin on it — worse than no map.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useGoogleMaps } from "../../GoogleMapsContext";
import DropoffPrecision, {
  describeDropoffPrecision,
} from "../DropoffPrecision";
import { reverseGeocode } from "../../utilities/reverseGeocode";
import { whatsAppLink } from "../../utilities/phoneNumber";

const DEFAULT_ZOOM = 16;
const MAP_HEIGHT = 260;

const isCoord = (value) => Number.isFinite(Number(value));

/** The one message, whichever way it is sent. */
export const buildPinMessage = ({ placeName, address, lat, lng, orderRef }) => {
  const mapLink = `https://maps.google.com/?q=${lat},${lng}`;
  const lines = [
    orderRef ? `Lusuku delivery ${orderRef}` : "Lusuku delivery",
    placeName || address ? `Drop-off: ${placeName || address}` : null,
    placeName && address && placeName !== address ? `Address: ${address}` : null,
    mapLink,
  ];
  return lines.filter(Boolean).join("\n");
};

const Chevron = ({ open }) => (
  <svg
    className={`lk-chevron ${open ? "lk-chevron--open" : ""}`}
    width="12"
    height="12"
    viewBox="0 0 12 12"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M2.5 4.25 6 7.75l3.5-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DropoffMap = ({
  lat,
  lng,
  address,
  shippingAddress,
  orderRef,
  rider,
  country,
}) => {
  const { mapsApiLoaded } = useGoogleMaps() || {};
  const [open, setOpen] = useState(false);
  const [placeName, setPlaceName] = useState(null);

  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  const hasPin = isCoord(lat) && isCoord(lng);
  const latNum = Number(lat);
  const lngNum = Number(lng);

  // Name the pin as soon as the Maps script is up — the name is the headline of
  // this block, so waiting for the map to be opened would leave the row reading
  // as coordinates for as long as nobody expanded it.
  useEffect(() => {
    if (!hasPin || !mapsApiLoaded) return undefined;
    let alive = true;
    reverseGeocode(latNum, lngNum).then((name) => {
      if (alive) setPlaceName(name);
    });
    return () => {
      alive = false;
    };
  }, [hasPin, mapsApiLoaded, latNum, lngNum]);

  useEffect(() => {
    if (!open || !mapsApiLoaded || !hasPin || !containerRef.current) return;
    if (!window.google?.maps) return;

    const position = { lat: latNum, lng: lngNum };

    if (!mapRef.current) {
      mapRef.current = new window.google.maps.Map(containerRef.current, {
        center: position,
        zoom: DEFAULT_ZOOM,
        disableDefaultUI: true,
        zoomControl: true,
        fullscreenControl: true,
        gestureHandling: "cooperative",
      });
    } else {
      mapRef.current.setCenter(position);
    }

    if (!markerRef.current) {
      markerRef.current = new window.google.maps.Marker({
        map: mapRef.current,
        position,
        title: placeName || address || "Drop-off",
      });
    } else {
      markerRef.current.setPosition(position);
    }
  }, [open, mapsApiLoaded, hasPin, latNum, lngNum, placeName, address]);

  useEffect(() => {
    if (open) return;
    if (markerRef.current) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }
    mapRef.current = null;
  }, [open]);

  if (!hasPin) return null;

  const message = buildPinMessage({
    placeName,
    address,
    lat: latNum,
    lng: lngNum,
    orderRef,
  });

  const riderPhone =
    rider?.phoneNumber || rider?.phone || rider?.contactMobile || null;
  const riderName = rider?.firstName
    ? `${rider.firstName} ${rider.lastName || ""}`.trim()
    : null;
  const shareHref = whatsAppLink(riderPhone, message, country);

  const copyPin = async () => {
    try {
      await navigator.clipboard.writeText(message);
      toast.success("Drop-off pin copied.");
    } catch (err) {
      toast.warn("Could not copy. Select the link and copy it manually.");
    }
  };

  const precision = describeDropoffPrecision(shippingAddress);

  return (
    <div className="lk-dropoff">
      <div className="lk-dropoff-head">
        <span
          className="lk-dropoff-name"
          title={`${latNum}, ${lngNum}`}
        >
          {placeName || address || `${latNum}, ${lngNum}`}
        </span>
        <DropoffPrecision shippingAddress={shippingAddress} compact />
      </div>

      <div className="rod-actions" style={{ marginTop: 8 }}>
        <button
          type="button"
          className="lk-action-btn lk-action-btn--quiet"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls="lk-dropoff-map"
        >
          <Chevron open={open} />
          {open ? "Hide map" : "Show map"}
        </button>

        {shareHref && (
          <a
            className="lk-action-btn"
            href={shareHref}
            target="_blank"
            rel="noopener noreferrer"
            title={
              riderName
                ? `Send this pin to ${riderName} on WhatsApp`
                : "Send this pin on WhatsApp"
            }
          >
            {riderName ? `Send pin to ${riderName.split(" ")[0]}` : "Send pin"}
          </a>
        )}

        <button
          type="button"
          className="lk-action-btn lk-action-btn--quiet"
          onClick={copyPin}
        >
          Copy pin
        </button>
      </div>

      {precision?.detail && (
        <div className="rod-hint" style={{ marginTop: 6 }}>
          {precision.detail}
        </div>
      )}

      {/* Height-animated wrapper. The map itself is only in the DOM while open,
          so the closing frame animates an empty box — which is why the wrapper
          fades as well as collapses. `prefers-reduced-motion` turns both off in
          the stylesheet. */}
      <div
        id="lk-dropoff-map"
        className={`lk-collapse ${open ? "lk-collapse--open" : ""}`}
        style={{ "--lk-collapse-h": `${MAP_HEIGHT}px` }}
      >
        <div className="lk-dropoff-map-frame">
          {open &&
            (mapsApiLoaded ? (
              <div
                ref={containerRef}
                style={{ width: "100%", height: MAP_HEIGHT }}
              />
            ) : (
              <div
                className="rod-hint"
                style={{
                  height: MAP_HEIGHT,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                The map service is still loading.
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default DropoffMap;
